import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Receipt,
  Download,
  ArrowLeft,
  AlertCircle,
  Coins,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { Button, Card, Badge, LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';

interface Purchase {
  id: string;
  pack_type: string;
  pack_name: string;
  credits_purchased: number;
  amount_paid: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  created_at: string | null;
  completed_at: string | null;
  receipt_url: string | null;
}

export function PurchaseHistoryPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const limit = 10;

  const fetchPurchases = useCallback(async (currentOffset: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.getPurchaseHistory({ limit, offset: currentOffset });
      setPurchases(response.purchases);
      setTotal(response.total);
      setHasMore(response.has_more);
    } catch (err) {
      setError('Failed to load purchase history. Please try again.');
      console.error('Failed to fetch purchases:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login', { replace: true, state: { from: { pathname: '/purchases' } } });
      return;
    }
    fetchPurchases(offset);
  }, [authLoading, isAuthenticated, navigate, fetchPurchases, offset]);

  const handlePrevPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (hasMore) {
      setOffset(offset + limit);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Completed</Badge>;
      case 'pending':
        return <Badge variant="warning">Pending</Badge>;
      case 'failed':
        return <Badge variant="error">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
            <Receipt className="w-6 h-6 text-primary" />
            Purchase History
          </h1>
          <p className="text-text-secondary text-sm sm:text-base mt-1">
            View your credit pack purchases and download receipts
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchPurchases(offset)}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </Button>
          <Link to="/dashboard">
            <Button variant="secondary" size="sm" leftIcon={<ArrowLeft className="w-4 h-4" />}>
              Dashboard
            </Button>
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <Card>
        {isLoading ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <p className="text-text-secondary">Loading purchase history...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center">
            <AlertCircle className="w-12 h-12 text-error mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Error Loading Purchases</h3>
            <p className="text-text-secondary mb-4">{error}</p>
            <Button onClick={() => fetchPurchases(offset)} variant="secondary">
              Try Again
            </Button>
          </div>
        ) : purchases.length === 0 ? (
          <div className="p-12 text-center">
            <Coins className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Purchases Yet</h3>
            <p className="text-text-secondary mb-4">
              You haven't purchased any credit packs yet.
            </p>
            <Link to="/dashboard">
              <Button variant="primary">View Credit Packs</Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-surface-light">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Pack
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider hidden sm:table-cell">
                      Credits
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Receipt
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {purchases.map((purchase) => (
                    <tr key={purchase.id} className="hover:bg-surface-light transition-colors">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm">
                        {formatDate(purchase.created_at)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div>
                          <span className="text-sm font-medium">
                            {purchase.pack_name}
                          </span>
                          <span className="sm:hidden block text-xs text-text-secondary">
                            {purchase.credits_purchased.toLocaleString()} credits
                          </span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm hidden sm:table-cell">
                        <span className="text-primary font-medium">
                          {purchase.credits_purchased.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {formatCurrency(purchase.amount_paid, purchase.currency)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(purchase.status)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right">
                        {purchase.receipt_url ? (
                          <a
                            href={purchase.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-light transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            <span className="hidden sm:inline">Download</span>
                          </a>
                        ) : purchase.status === 'completed' ? (
                          <span className="text-sm text-text-tertiary">N/A</span>
                        ) : (
                          <span className="text-sm text-text-tertiary">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="px-4 sm:px-6 py-4 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-sm text-text-secondary">
                  Showing {offset + 1} - {Math.min(offset + purchases.length, total)} of {total} purchases
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={offset === 0}
                    leftIcon={<ChevronLeft className="w-4 h-4" />}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={!hasMore}
                    rightIcon={<ChevronRight className="w-4 h-4" />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Help Section */}
      <Card className="p-4 sm:p-6 bg-primary/5 border-primary/20">
        <h3 className="text-sm font-medium mb-2">Need Help?</h3>
        <p className="text-sm text-text-secondary">
          If you have questions about a purchase or need assistance with receipts,
          please contact us at{' '}
          <a href="mailto:contact@vettis.ai" className="text-primary hover:text-primary-light underline transition-colors">
            contact@vettis.ai
          </a>
        </p>
      </Card>
    </div>
  );
}

